/**
 * Push notifications — pure form ↔ wire helpers for `POST /admin/push/*`.
 */

export type PushAudience = 'users' | 'listeners' | 'user';

export const TITLE_MAX = 65;
export const BODY_MAX = 240;
export const IMAGE_TYPES = ['image/png', 'image/jpeg'];
export const IMAGE_MAX_BYTES = 1024 * 1024;

export interface PushForm {
  audience: PushAudience;
  userRef: string;
  title: string;
  body: string;
  imageKey: string | null;
  /** Local object URL of the uploaded file, for the preview only. */
  imageUrl: string | null;
  deepLink: string;
}

export const EMPTY_PUSH: PushForm = {
  audience: 'users',
  userRef: '',
  title: '',
  body: '',
  imageKey: null,
  imageUrl: null,
  deepLink: '',
};

export const AUDIENCE_LABEL: Record<PushAudience, string> = {
  users: 'All users',
  listeners: 'All listeners',
  user: 'One user',
};

/** The first problem with the form, or null when it can be sent. */
export function pushFormError(f: PushForm): string | null {
  if (f.audience === 'user' && !f.userRef.trim()) return 'Enter the user ID or @handle.';
  if (!f.title.trim()) return 'Enter a title.';
  if (f.title.trim().length > TITLE_MAX) return `The title is limited to ${TITLE_MAX} characters.`;
  if (!f.body.trim()) return 'Enter a message.';
  if (f.body.trim().length > BODY_MAX) return `The message is limited to ${BODY_MAX} characters.`;
  const link = f.deepLink.trim();
  if (link && !/^(maja:\/\/|\/)/.test(link)) return 'The link must start with maja:// or /.';
  return null;
}

/** Why this file can't be a push image, or null when it can. */
export function pushImageProblem(file: { type: string; size: number }): string | null {
  if (!IMAGE_TYPES.includes(file.type)) return 'Use a PNG or JPEG image.';
  if (file.size > IMAGE_MAX_BYTES) return 'The image is too large — the limit is 1 MB.';
  return null;
}

export function previewBody(f: PushForm) {
  return { audience: f.audience, userRef: f.audience === 'user' ? f.userRef.trim() : null };
}

export function sendBody(f: PushForm) {
  return {
    ...previewBody(f),
    title: f.title.trim(),
    body: f.body.trim(),
    imageKey: f.imageKey,
    deepLink: f.deepLink.trim() || null,
  };
}
